<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { SidebarMenuItem, MenuPermission, MenuClickEvent } from '@/types/sidebar-menu.types'
import JDynamicMenuItem from './JSidebarSimple/JDynamicMenuItem.vue'
import JInput from '@/components/atoms/JInput.vue'
import JIcon from '@/components/atoms/JIcon.vue'
import { cn } from '@/lib/utils'

/**
 * JSidebarAdvanced - 고급 사이드바 컴포넌트
 * Advanced Sidebar Component
 * 
 * @description
 * 검색, 즐겨찾기, 다단계 메뉴를 지원하는 고급 사이드바 컴포넌트입니다.
 * 기본 메뉴와 즐겨찾기 탭을 제공합니다.
 * 
 * @example
 * ```vue
 * <JSidebarAdvanced
 *   :menu-items="menuItems"
 *   :permissions="userPermissions"
 *   :favorites="favoriteMenuKeys"
 *   @menu-click="handleMenuClick"
 *   @favorite-change="handleFavoriteChange"
 * />
 * ```
 * 
 * @example JSON 메뉴 데이터 예시
 * ```json
 * [
 *   {
 *     "label": "대시보드",
 *     "icon": "house",
 *     "menuType": "L",
 *     "menuKey": 1,
 *     "path": "/dashboard"
 *   },
 *   {
 *     "label": "재고 관리",
 *     "icon": "package",
 *     "menuType": "F",
 *     "menuKey": 2,
 *     "children": [
 *       {
 *         "label": "재고 현황",
 *         "menuType": "L",
 *         "menuKey": 21,
 *         "path": "/inventory/status"
 *       },
 *       {
 *         "label": "입고 관리",
 *         "menuType": "F",
 *         "menuKey": 22,
 *         "children": [
 *           {
 *             "label": "입고 등록",
 *             "menuType": "L",
 *             "menuKey": 221,
 *             "path": "/inventory/receiving/register"
 *           }
 *         ]
 *       }
 *     ]
 *   }
 * ]
 * ```
 */

type TabType = 'menu' | 'favorites'

type StyleType = 'default' | 'minimal'

const props = withDefaults(
  defineProps<{
    /** 메뉴 아이템 목록 */
    menuItems: SidebarMenuItem[]
    /** 권한 목록 */
    permissions?: MenuPermission[]
    /** 즐겨찾기 메뉴 키 목록 */
    favorites?: (number | string)[]
    /** 스타일 타입 */
    styletype?: StyleType
    /** 추가 CSS 클래스 */
    class?: string
    /** 너비 */
    width?: string
    /** 표시 여부 */
    isVisible?: boolean
  }>(),
  {
    permissions: () => [],
    favorites: () => [],
    styletype: 'minimal',
    width: '280px',
    isVisible: true,
  },
)

const emit = defineEmits<{
  /** 메뉴 클릭 이벤트 */
  menuClick: [event: MenuClickEvent]
  /** 즐겨찾기 변경 이벤트 */
  favoriteChange: [menuKey: number | string | undefined, isFavorite: boolean]
}>()

// vue-router가 설정되지 않은 경우를 대비 (Storybook에서 router가 제공됨)
const route = useRoute()

/**
 * 현재 활성 탭
 */
const activeTab = ref<TabType>('menu')

/**
 * 검색어
 */
const searchQuery = ref('')

/**
 * 현재 활성화된 경로
 */
const activePath = computed(() => route.path)

/**
 * 확장된 메뉴 키 목록
 */
const expandedKeys = ref<Set<number | string>>(new Set())

/**
 * 즐겨찾기 메뉴 아이템 목록
 * 즐겨찾기는 dept 없이 1단계로만 평탄화하여 표시
 */
const favoriteMenuItems = computed(() => {
  if (!Array.isArray(props.favorites) || props.favorites.length === 0) {
    return []
  }

  if (!Array.isArray(props.menuItems) || props.menuItems.length === 0) {
    return []
  }

  /**
   * 메뉴 아이템을 재귀적으로 순회하며 즐겨찾기만 추출
   * 즐겨찾기에서는 dept 없이 1단계로만 평탄화
   * menuType L(Link)만 포함하고 F(Folder)는 제외
   */
  const flattenFavorites = (items: SidebarMenuItem[]): SidebarMenuItem[] => {
    const result: SidebarMenuItem[] = []

    if (!Array.isArray(items)) {
      return result
    }

    for (const item of items) {
      const key = item.menuKey || item.label
      const isFavorite = Array.isArray(props.favorites) && props.favorites.includes(key)

      // 즐겨찾기이고 menuType이 L(Link)인 경우만 추가 (F는 제외)
      if (isFavorite && item.menuType === 'L') {
        result.push({
          ...item,
          children: undefined, // children 제거하여 1단계로만 표시
        })
      }

      // 하위 메뉴도 재귀적으로 탐색
      if (item.children && Array.isArray(item.children) && item.children.length > 0) {
        const childFavorites = flattenFavorites(item.children)
        result.push(...childFavorites)
      }
    }

    return result
  }

  return flattenFavorites(props.menuItems)
})

/**
 * 검색어로 필터링된 메뉴 아이템
 * 재귀적으로 children까지 검색
 */
const filteredMenuItems = computed(() => {
  if (!Array.isArray(props.menuItems) || props.menuItems.length === 0) {
    return []
  }

  if (!searchQuery.value || searchQuery.value.trim() === '') {
    return props.menuItems
  }

  const query = searchQuery.value.toLowerCase().trim()

  /**
   * 메뉴 아이템을 재귀적으로 검색
   */
  const searchInMenu = (items: SidebarMenuItem[]): SidebarMenuItem[] => {
    const result: SidebarMenuItem[] = []

    if (!Array.isArray(items)) {
      return result
    }

    for (const item of items) {
      const matchesLabel = item.label?.toLowerCase().includes(query) ?? false

      // 하위 메뉴 검색
      let filteredChildren: SidebarMenuItem[] | undefined = undefined
      if (item.children && Array.isArray(item.children) && item.children.length > 0) {
        filteredChildren = searchInMenu(item.children)
      }

      // 현재 메뉴나 하위 메뉴 중 하나라도 매칭되면 포함
      if (matchesLabel || (Array.isArray(filteredChildren) && filteredChildren.length > 0)) {
        result.push({
          ...item,
          children: filteredChildren,
        })
      }
    }

    return result
  }

  return searchInMenu(props.menuItems)
})

/**
 * 검색 결과에 따라 부모 메뉴 자동 확장
 * computed 외부에서 watch를 통해 처리
 */
watch(
  () => filteredMenuItems.value,
  (filtered) => {
    if (!searchQuery.value || searchQuery.value.trim() === '') {
      return
    }

    // 검색 결과에서 매칭된 하위 메뉴가 있는 부모를 찾아 확장
    const findParentsWithMatches = (items: SidebarMenuItem[]): Set<number | string> => {
      const keysToExpand = new Set<number | string>()

      const traverse = (menuItems: SidebarMenuItem[]): void => {
        if (!Array.isArray(menuItems)) {
          return
        }

        for (const item of menuItems) {
          if (item.children && Array.isArray(item.children) && item.children.length > 0) {
            const key = item.menuKey || item.label
            keysToExpand.add(key)
            traverse(item.children)
          }
        }
      }

      traverse(items)
      return keysToExpand
    }

    const keysToExpand = findParentsWithMatches(filtered)
    keysToExpand.forEach(key => {
      expandedKeys.value.add(key)
    })
  },
  { immediate: false }
)

/**
 * 검색어로 필터링된 즐겨찾기 메뉴 아이템
 * 즐겨찾기는 이미 평탄화되어 있으므로 단순 필터링만 수행
 */
const filteredFavoriteItems = computed(() => {
  if (!Array.isArray(favoriteMenuItems.value) || favoriteMenuItems.value.length === 0) {
    return []
  }

  if (!searchQuery.value || searchQuery.value.trim() === '') {
    return favoriteMenuItems.value
  }

  const query = searchQuery.value.toLowerCase().trim()

  // 즐겨찾기는 이미 평탄화되어 1단계이므로 단순 필터링만 수행
  return favoriteMenuItems.value.filter((item) =>
    item.label?.toLowerCase().includes(query) ?? false
  )
})


/**
 * 탭 변경 핸들러
 * 탭 전환 시 검색 쿼리를 초기화하여 각 탭의 독립적인 검색 상태 유지
 */
const handleTabChange = (tab: TabType) => {
  if (activeTab.value !== tab) {
    activeTab.value = tab
    // 탭 전환 시 검색 쿼리 초기화 (선택적 - UX 고려)
    // 검색 쿼리를 유지하려면 아래 라인을 제거하세요
    searchQuery.value = ''
  }
}

/**
 * 확장 상태 변경 핸들러
 */
const handleExpandChange = (menuKey: number | string | undefined, expanded: boolean) => {
  if (!menuKey) return

  if (expanded) {
    expandedKeys.value.add(menuKey)
  } else {
    expandedKeys.value.delete(menuKey)
  }
}

/**
 * 메뉴 클릭 핸들러
 */
const handleMenuClick = (event: MenuClickEvent) => {
  emit('menuClick', event)
}

/**
 * 즐겨찾기 토글 핸들러
 */
const handleFavoriteToggle = (menuKey: number | string | undefined) => {
  if (!menuKey) return

  const isFavorite = props.favorites?.includes(menuKey) ?? false
  emit('favoriteChange', menuKey, !isFavorite)
}

/**
 * 메뉴 아이템에서 특정 menuKey를 찾는 헬퍼 함수
 */
const findMenuItemByKey = (items: SidebarMenuItem[], targetKey: number | string): SidebarMenuItem | null => {
  for (const item of items) {
    const key = item.menuKey || item.label
    if (key === targetKey) {
      return item
    }
    if (item.children && item.children.length > 0) {
      const found = findMenuItemByKey(item.children, targetKey)
      if (found) return found
    }
  }
  return null
}

/**
 * 메뉴가 즐겨찾기인지 확인 (L 타입만 즐겨찾기 가능)
 */
const isFavorite = (menuKey: number | string | undefined): boolean => {
  if (!menuKey) return false
  if (!props.favorites?.includes(menuKey)) return false
  
  // menuType이 L인 경우만 즐겨찾기로 인정
  const menuItem = findMenuItemByKey(props.menuItems, menuKey)
  return menuItem?.menuType === 'L'
}

/**
 * 스타일 프리셋
 */
const STYLE_PRESETS: Record<StyleType, {
  containerClass: string
  tabContainerClass: string
  tabButtonClass: string
  searchContainerClass: string
  menuContainerClass: string
}> = {
  default: {
    containerClass: 'h-full bg-background border-r border-border flex flex-col',
    tabContainerClass: 'flex border-b border-border',
    tabButtonClass: 'flex-1 px-3 py-1.5 text-xs font-medium transition-colors border-b-2 hover:bg-accent/50',
    searchContainerClass: 'p-1.5 border-b border-border',
    menuContainerClass: 'flex-1 overflow-y-auto p-1.5 space-y-0.5',
  },
  minimal: {
    containerClass: 'h-full bg-background border-r border-border flex flex-col',
    tabContainerClass: 'flex border-b border-border',
    tabButtonClass: 'flex-1 px-2 py-1 text-xs font-medium transition-colors border-b-2',
    searchContainerClass: 'p-1 border-b border-border',
    menuContainerClass: 'flex-1 overflow-y-auto p-1 space-y-0.5',
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

/**
 * 루트 클래스
 */
const rootClasses = computed(() => {
  return cn(
    preset.value.containerClass,
    props.class
  )
})
</script>

<template>
  <Transition name="slide">
    <aside v-show="props.isVisible" :class="rootClasses" :style="{ width }">
    <!-- 탭 헤더 -->
    <div :class="preset.tabContainerClass">
      <button
        :class="cn(
          preset.tabButtonClass,
          activeTab === 'menu'
            ? 'border-primary text-primary'
            : 'border-transparent text-muted-foreground hover:text-foreground'
        )"
        @click="handleTabChange('menu')"
      >
        기본메뉴
      </button>
      <button
        :class="cn(
          preset.tabButtonClass,
          activeTab === 'favorites'
            ? 'border-primary text-primary'
            : 'border-transparent text-muted-foreground hover:text-foreground'
        )"
        @click="handleTabChange('favorites')"
      >
        즐겨찾기
      </button>
    </div>

    <!-- 검색 영역 -->
    <div :class="preset.searchContainerClass">
      <div class="relative">
        <JIcon
          name="search"
          size="sm"
          class="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <JInput
          v-model="searchQuery"
          placeholder="메뉴 검색..."
          :class="cn(
            'pl-8',
            props.styletype === 'minimal' && 'h-8 text-xs'
          )"
        />
      </div>
    </div>

    <!-- 메뉴 목록 -->
    <div :class="preset.menuContainerClass">
      <template v-if="activeTab === 'menu'">
        <template v-if="filteredMenuItems.length > 0">
          <div
            v-for="(item, index) in filteredMenuItems"
            :key="item.menuKey || item.label || index"
            class="flex items-center group"
          >
            <JDynamicMenuItem
              :item="item"
              :level="0"
              :permissions="permissions"
              :active-path="activePath"
              :expanded-keys="expandedKeys"
              :favorites="favorites"
              :on-favorite-toggle="handleFavoriteToggle"
              :is-favorite="isFavorite"
              :styletype="styletype"
              class="flex-1"
              @menu-click="handleMenuClick"
              @expand-change="handleExpandChange"
            />
          </div>
        </template>
        <div v-else class="text-center py-8 text-muted-foreground">
          <p class="text-xs">검색 결과가 없습니다.</p>
        </div>
      </template>

      <template v-else>
        <template v-if="filteredFavoriteItems.length > 0">
          <JDynamicMenuItem
            v-for="(item, index) in filteredFavoriteItems"
            :key="item.menuKey || item.label || index"
            :item="item"
            :level="0"
            :permissions="permissions"
            :active-path="activePath"
            :expanded-keys="expandedKeys"
            :styletype="styletype"
            @menu-click="handleMenuClick"
            @expand-change="handleExpandChange"
          />
        </template>
        <div v-else class="text-center py-8 text-muted-foreground">
          <p class="text-xs">즐겨찾기가 없습니다.</p>
        </div>
      </template>
    </div>
  </aside>
  </Transition>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}
</style>
